(() => {
  'use strict';
  if (customElements.get('zv-p1-pro')) return;

/* Add one selected P1-dongle through Shopify's locale-aware cart API.
 * Native return_to remains the fallback; existing cart contents stay intact.
 * https://shopify.dev/docs/api/ajax/reference/cart
 * https://shopify.dev/docs/api/liquid/tags/form#form-return_to
 */
function createP1Commerce(root, { signal } = {}) {
  const form = root.querySelector('.p1-product-form');
  if (!form) return { syncAvailability() {}, reset() {}, destroy() {} };

  const buttons = Array.from(root.querySelectorAll('[data-p1-buy]'))
    .filter((button) => button.form === form);
  const variant = form.querySelector('[name="id"]');
  const initialAvailability = buttons.some((button) => !button.disabled);
  const variantWasDisabled = Boolean(variant?.disabled);
  const originalLabels = new Map(buttons.map((button) => [button,
    button.dataset.p1Label || button.querySelector('[data-p1-buy-label]')?.textContent.trim() ||
    (button.dataset.p1PurchaseAction === 'checkout' ? window.ZinVoltI18n.t("buy_now_b06a19") : window.ZinVoltI18n.t("add_to_cart_646feb"))
  ]));
  const shopRoot = `${window.Shopify?.routes?.root || '/'}`.replace(/\/?$/, '/');
  const cartURL = window.routes?.cart_url || `${shopRoot}cart`;
  const addURL = `${shopRoot}cart/add.js`;
  const checkoutURL = `${shopRoot}checkout`;
  let busy = false;
  let activeAction = null;
  let requestController = null;
  let requestNumber = 0;
  let destroyed = false;
  let status = root.querySelector('[data-p1-commerce-status]');

  if (!status) {
    status = document.createElement('p');
    status.className = 'p1-commerce-status';
    status.dataset.p1CommerceStatus = '';
    status.hidden = true;
    status.tabIndex = -1;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    form.insertAdjacentElement('afterend', status);
  }

  function available() {
    if (!variant?.value || !/^[1-9]\d*$/.test(variant.value)) return false;
    const selected = variant.selectedOptions?.[0];
    return selected ? selected.dataset.available === 'true' : initialAvailability;
  }

  function syncAvailability() {
    const inStock = available();
    buttons.forEach((button) => {
      button.disabled = busy || !inStock;
      button.setAttribute('aria-disabled', String(button.disabled));
      button.toggleAttribute('aria-busy', busy);
      const label = button.querySelector('[data-p1-buy-label]');
      if (label) {
        label.textContent = !inStock ? window.ZinVoltI18n.t("temporarily_sold_out_8564f7") : busy
          ? (activeAction === 'checkout' ? window.ZinVoltI18n.t("going_to_checkout_671b53") : window.ZinVoltI18n.t("adding_774ee6"))
          : originalLabels.get(button);
      }
    });
  }

  function setBusy(value, action = null) {
    busy = value;
    activeAction = action;
    form.toggleAttribute('aria-busy', value);
    root.toggleAttribute('data-p1-purchasing', value);
    if (variant?.tagName === 'SELECT') variant.disabled = value || variantWasDisabled;
    syncAvailability();
  }

  function message(text, { error = false, link = null, focus = false } = {}) {
    status.replaceChildren(document.createTextNode(text));
    status.dataset.state = error ? 'error' : 'success';
    if (link) {
      const anchor = document.createElement('a');
      anchor.href = link.href;
      anchor.textContent = link.label;
      anchor.className = 'zv-text-link';
      status.append(document.createTextNode(' '), anchor);
    }
    status.hidden = !text;
    if (focus) {
      status.focus({ preventScroll: true });
      status.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }

  function reset() {
    requestNumber += 1;
    requestController?.abort();
    requestController = null;
    setBusy(false);
  }

  async function submit(event) {
    if (event.defaultPrevented) return;
    if (!form.checkValidity()) return;
    // If enhancement is unavailable, retain the native form and its return_to.
    if (typeof fetch !== 'function' || typeof FormData !== 'function') return;
    event.preventDefault();
    if (busy || !available() || destroyed) return;

    const submitter = event.submitter;
    const action = submitter?.dataset.p1PurchaseAction === 'checkout' ? 'checkout' : 'cart';
    const payload = new FormData(form);
    payload.set('quantity', '1');
    payload.delete('return_to');
    const drawer = action === 'cart' ? document.querySelector('cart-drawer') : null;
    let sectionIDs = [];
    if (drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.renderContents === 'function') {
      sectionIDs = drawer.getSectionsToRender().map((section) => section.id).filter(Boolean).slice(0, 5);
      if (sectionIDs.length) {
        payload.set('sections', sectionIDs.join(','));
        payload.set('sections_url', window.location.pathname);
      }
      drawer.setActiveElement?.(submitter || document.activeElement);
    }

    const thisRequest = ++requestNumber;
    const controller = new AbortController();
    requestController = controller;
    const timeout = window.setTimeout(() => controller.abort(), 25000);
    let added = false;
    let leaving = false;
    setBusy(true, action);
    message('');

    try {
      const response = await fetch(addURL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: payload,
        signal: controller.signal
      });
      const result = await response.json().catch(() => null);
      if (thisRequest !== requestNumber || destroyed) return;

      // A 400 or network failure can still follow a successful cart mutation.
      // Never automatically repeat the POST or claim that the cart is unchanged.
      if (!response.ok || !result || result.status) {
        const error = new Error('Cart addition was not confirmed');
        error.customerMessage = response.status === 422 && typeof result?.description === 'string'
          ? result.description
          : window.ZinVoltI18n.t("we_could_not_confirm_that_the_item_f3f52e");
        throw error;
      }
      added = true;

      if (action === 'checkout') {
        // Add only; keep other line items, notes, discounts and cart attributes.
        // The customer goes directly to checkout without visiting the cart page.
        window.location.assign(checkoutURL);
        leaving = true;
        return;
      }

      const completeDrawer = sectionIDs.length > 0 && sectionIDs.every((id) =>
        typeof result.sections?.[id] === 'string' && result.sections[id].trim()
      );
      if (drawer && completeDrawer) {
        try {
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
          message(window.ZinVoltI18n.t("the_p1_dongle_pro_is_in_your_78eea1"));
          return;
        } catch (_) {
          // The item is already added. Navigate to the cart, never add again.
        }
      }
      window.location.assign(cartURL);
      leaving = true;
    } catch (error) {
      if (thisRequest !== requestNumber || destroyed) return;
      if (added) {
        message(window.ZinVoltI18n.t("the_p1_dongle_pro_has_been_added_94b399"), {
          link: { href: action === 'checkout' ? checkoutURL : cartURL,
            label: action === 'checkout' ? window.ZinVoltI18n.t("go_to_checkout_55a15b") : window.ZinVoltI18n.t("view_your_cart_d6f557") },
          focus: true
        });
      } else {
        message(error.customerMessage || window.ZinVoltI18n.t("we_could_not_confirm_that_the_item_f3f52e"), {
          error: true,
          link: { href: cartURL, label: window.ZinVoltI18n.t("view_your_cart_d6f557") },
          focus: true
        });
      }
    } finally {
      window.clearTimeout(timeout);
      if (thisRequest === requestNumber) {
        requestController = null;
        // Keep all order buttons locked until navigation; pageshow restores them.
        if (!leaving && !destroyed) setBusy(false);
      }
    }
  }

  function onPageShow(event) {
    if (event.persisted) reset();
  }

  function destroy() {
    destroyed = true;
    reset();
    form.removeEventListener('submit', submit);
    window.removeEventListener('pageshow', onPageShow);
  }

  form.addEventListener('submit', submit);
  window.addEventListener('pageshow', onPageShow);
  signal?.addEventListener('abort', destroy, { once: true });
  syncAvailability();
  return { syncAvailability, reset, destroy };
}


  const CHART_DAYS = {
    sunny: {
      export: 'M0 198 C60 198 100 198 140 194 S195 135 230 86 S285 28 320 45 S390 115 425 164 S485 196 600 198',
      import: 'M0 174 C55 174 75 183 105 176 S140 125 175 137 S210 186 250 190 S360 193 410 188 S450 138 480 111 S525 154 555 166 S585 174 600 170',
      caption: window.ZinVoltI18n.t("see_how_grid_import_and_export_can_577c9e")
    },
    cloudy: {
      export: 'M0 198 C80 198 120 198 160 196 S205 188 230 169 S255 189 280 174 S305 151 330 180 S355 164 385 188 S465 198 600 198',
      import: 'M0 174 C55 175 80 181 105 173 S145 122 175 135 S210 158 235 145 S265 164 295 147 S330 164 360 149 S410 161 440 132 S475 97 505 120 S555 168 600 170',
      caption: window.ZinVoltI18n.t("see_how_grid_import_and_export_can_577c9e")
    }
  };



  const PATTERN_DAYS = {
    sunny: {
      caption: window.ZinVoltI18n.t("compare_your_daily_pattern_in_sunny_and_3224b1"),
      slots: {
        morning: { title: window.ZinVoltI18n.t("the_day_begins_d66ae4"), copy: window.ZinVoltI18n.t("you_start_drawing_power_from_the_grid_c6fc10"), import: '72%', export: '12%' },
        midday: { title: window.ZinVoltI18n.t("surplus_solar_power_a820ba"), copy: window.ZinVoltI18n.t("your_surplus_is_exported_to_the_grid_808536"), import: '18%', export: '92%' },
        evening: { title: window.ZinVoltI18n.t("power_needed_again_a0a477"), copy: window.ZinVoltI18n.t("your_grid_import_increases_again_a0d682"), import: '84%', export: '0%' },
        night: { title: window.ZinVoltI18n.t("a_quieter_home_e8e599"), copy: window.ZinVoltI18n.t("your_consumption_without_solar_generation_6ec981"), import: '22%', export: '0%' }
      }
    },
    cloudy: {
      caption: window.ZinVoltI18n.t("less_sunshine_means_less_grid_export_in_ff12b6"),
      slots: {
        morning: { title: window.ZinVoltI18n.t("the_day_begins_d66ae4"), copy: window.ZinVoltI18n.t("you_draw_more_power_from_the_grid_f8a4f4"), import: '78%', export: '6%' },
        midday: { title: window.ZinVoltI18n.t("less_solar_power_fbeb51"), copy: window.ZinVoltI18n.t("there_is_less_surplus_power_1ddc6a"), import: '36%', export: '48%' },
        evening: { title: window.ZinVoltI18n.t("power_needed_again_a0a477"), copy: window.ZinVoltI18n.t("your_grid_import_increases_again_a0d682"), import: '88%', export: '0%' },
        night: { title: window.ZinVoltI18n.t("a_quieter_home_e8e599"), copy: window.ZinVoltI18n.t("your_consumption_without_solar_generation_6ec981"), import: '22%', export: '0%' }
      }
    }
  };

  class ZinVoltP1Pro extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.activeAnimations = new Set();
      this.variantSelect = this.querySelector('select[data-p1-variant]');
      this.sticky = this.querySelector('[data-p1-sticky]');
      this.purchase = this.querySelector('[data-p1-purchase]');
      this.tabs = Array.from(this.querySelectorAll('[data-p1-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-p1-panel]'));
      this.commerce = createP1Commerce(this, { signal });

      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', () => this.updateVariant(true), { signal });
        this.updateVariant(false);
      }
      this.setupTabs(signal);
      this.initFaq(signal);
      this.setupMotion(signal);
      this.querySelectorAll('[data-p1-period]').forEach((button) => {
        button.addEventListener('click', () => this.updateChart(button.dataset.p1Period), { signal });
      });
      this.querySelectorAll('.p1-setup-steps details').forEach((details) => {
        details.addEventListener('toggle', () => {
          if (details.open) this.animateElement(details.querySelector('summary + div'),
            [{ opacity: .3, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'translateY(0)' }],
            { duration: 200, easing: 'ease-out' });
        }, { signal });
      });
      this.navItems = Array.from(this.querySelectorAll('.p1-nav a[href^="#"]')).map((link) => ({
        link, section: this.querySelector(link.getAttribute('href'))
      })).filter((item) => item.section);
      this.scheduleScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateSticky();
          this.updateNav();
        });
      };
      window.addEventListener('scroll', this.scheduleScroll, { passive: true, signal });
      window.addEventListener('resize', this.scheduleScroll, { passive: true, signal });
      this.scheduleScroll();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.commerce?.destroy();
      this.commerce = null;
      this.revealObserver?.disconnect();
      this.activeAnimations?.forEach((animation) => animation.cancel());
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
      if (this.panelFrame) cancelAnimationFrame(this.panelFrame);
      if (this.sticky) this.sticky.hidden = true;
      this.scrollFrame = null;
      this.pointerFrame = null;
      this.panelFrame = null;
      this.revealObserver = null;
      this.controller = null;
      this.classList.remove('is-ready');
    }

    updateVariant(updateUrl) {
      const option = this.variantSelect?.selectedOptions[0];
      if (!option) return;
      if (option.dataset.price) {
        this.querySelectorAll('[data-p1-price]').forEach((price) => { price.textContent = option.dataset.price; });
      }
      this.commerce?.syncAvailability();
      if (updateUrl && option.value) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        try { window.history.replaceState(window.history.state, '', url.href); } catch (_) {}
      }
    }

    animateElement(element, frames, options) {
      if (!element || this.motionQuery.matches || typeof element.animate !== 'function') return null;
      const animation = element.animate(frames, options);
      this.activeAnimations.add(animation);
      animation.finished.catch(() => {}).finally(() => this.activeAnimations?.delete(animation));
      return animation;
    }

    initFaq(signal) {
      this.querySelectorAll('[data-zv-faq]').forEach((faq) => {
        if (faq.dataset.zvcFaqReady === 'true') return;
        faq.dataset.zvcFaqReady = 'true';
        const items = Array.from(faq.querySelectorAll('details'));
        const desired = new Map(items.map((item) => [item, item.open]));
        const pending = new Map();
        const transition = (item, open) => {
          const content = item.querySelector('[data-zv-faq-content]');
          if (!content) return;
          const height = item.open ? content.getBoundingClientRect().height : 0;
          pending.get(item)?.();
          desired.set(item, open);
          const finish = () => {
            if (desired.get(item) !== open) return;
            item.open = open;
            content.style.height = open ? 'auto' : '0px';
          };
          if (this.motionQuery.matches) { finish(); return; }
          // Use the shared FAQ's height transition and 420 ms easing. The
          // interrupted height is retained when a customer clicks quickly.
          item.open = true;
          content.style.height = `${height}px`;
          content.getBoundingClientRect();
          content.style.height = open ? `${content.scrollHeight}px` : '0px';
          const onEnd = (event) => {
            if (event && (event.target !== content || event.propertyName !== 'height')) return;
            cleanup();
            finish();
          };
          const timer = window.setTimeout(() => onEnd(), 480);
          const cleanup = () => {
            window.clearTimeout(timer);
            content.removeEventListener('transitionend', onEnd);
            pending.delete(item);
          };
          pending.set(item, cleanup);
          content.addEventListener('transitionend', onEnd);
        };
        let foundOpen = false;
        items.forEach((item) => {
          const summary = item.querySelector('summary');
          const content = item.querySelector('[data-zv-faq-content]');
          if (!summary || !content) return;
          if (item.open && foundOpen) { item.open = false; desired.set(item, false); }
          if (item.open) foundOpen = true;
          content.style.height = item.open ? 'auto' : '0px';
          summary.addEventListener('click', (event) => {
            event.preventDefault();
            if (desired.get(item)) { transition(item, false); return; }
            items.forEach((other) => { if (other !== item && desired.get(other)) transition(other, false); });
            transition(item, true);
          }, { signal });
        });
        signal.addEventListener('abort', () => {
          pending.forEach((cleanup) => cleanup());
          items.forEach((item) => {
            item.open = desired.get(item);
            const content = item.querySelector('[data-zv-faq-content]');
            if (content) content.style.height = item.open ? 'auto' : '0px';
          });
          delete faq.dataset.zvcFaqReady;
        }, { once: true });
      });
    }

    setupTabs(signal) {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.p1Tab), { signal });
        tab.addEventListener('keydown', (event) => {
          let target;
          if (['ArrowDown', 'ArrowRight'].includes(event.key)) target = (index + 1) % this.tabs.length;
          if (['ArrowUp', 'ArrowLeft'].includes(event.key)) target = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = this.tabs.length - 1;
          if (target === undefined) return;
          event.preventDefault();
          this.activateTab(this.tabs[target].dataset.p1Tab, true);
        }, { signal });
      });
      this.activateTab(this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.p1Tab || 'use');
    }

    activateTab(key, focus = false) {
      const selected = this.tabs.find((tab) => tab.dataset.p1Tab === key);
      if (!selected) return;
      this.tabs.forEach((tab) => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.p1Panel === key;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      if (key === 'pattern') this.drawChart();
      if (focus) selected.focus({ preventScroll: true });
    }

    updateChart(period) {
      const day = CHART_DAYS[period];
      const pattern = PATTERN_DAYS[period];
      if (!day && !pattern) return;
      this.querySelectorAll('[data-p1-period]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.p1Period === period));
      });
      if (day) {
        this.querySelector('[data-p1-export-line]')?.setAttribute('d', day.export);
        this.querySelector('[data-p1-import-line]')?.setAttribute('d', day.import);
      }
      if (pattern) {
        Object.entries(pattern.slots).forEach(([slot, values]) => {
          this.querySelector(`[data-p1-slot="${slot}"] [data-p1-slot-title]`)?.replaceChildren(document.createTextNode(values.title));
          this.querySelector(`[data-p1-slot="${slot}"] [data-p1-slot-copy]`)?.replaceChildren(document.createTextNode(values.copy));
          this.querySelector(`[data-p1-meter="${slot}-import"]`)?.style.setProperty('--meter-fill', values.import);
          this.querySelector(`[data-p1-meter="${slot}-export"]`)?.style.setProperty('--meter-fill', values.export);
        });
      }
      const caption = this.querySelector('[data-p1-chart-caption]');
      if (caption) caption.textContent = pattern?.caption || day?.caption || '';
      this.drawChart();
    }

    drawChart() {
      const line = this.querySelector('[data-p1-export-line]');
      if (line && !this.motionQuery.matches && typeof line.getTotalLength === 'function') {
        const length = line.getTotalLength();
        this.animateElement(line, [{ strokeDasharray: `${length} ${length}`, strokeDashoffset: length },
          { strokeDasharray: `${length} ${length}`, strokeDashoffset: 0 }], { duration: 650, easing: 'ease-out' });
      }
      this.animateElement(this.querySelector('[data-p1-import-line]'), [{ opacity: .15 }, { opacity: 1 }], { duration: 450 });
    }

    setupMotion(signal) {
      const reveals = Array.from(this.querySelectorAll('[data-p1-reveal]'));
      const showAll = () => reveals.forEach((element) => element.classList.add('is-visible'));
      if ('IntersectionObserver' in window && !this.motionQuery.matches) {
        try {
          this.revealObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              this.revealObserver.unobserve(entry.target);
              if (entry.target.classList.contains('p1-explorer')) {
                const active = this.panels.find((panel) => !panel.hidden);
                active?.classList.remove('is-active');
                this.panelFrame = requestAnimationFrame(() => { active?.classList.add('is-active'); this.panelFrame = null; });
              }
            });
          }, { threshold: .08, rootMargin: '0px 0px -24px 0px' });
          reveals.forEach((element) => this.revealObserver.observe(element));
        } catch (_) { showAll(); }
      } else showAll();
      const visual = this.querySelector('[data-p1-parallax]');
      const resetPointer = () => {
        if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
        this.pointerFrame = null;
        visual?.style.setProperty('--p1-move-x', '0px');
        visual?.style.setProperty('--p1-move-y', '0px');
      };
      visual?.addEventListener('pointermove', (event) => {
        if (this.motionQuery.matches || !this.pointerQuery.matches) return;
        const bounds = visual.getBoundingClientRect();
        const x = Math.max(-1, Math.min(1, ((event.clientX - bounds.left) / bounds.width - .5) * 2)) * 5;
        const y = Math.max(-1, Math.min(1, ((event.clientY - bounds.top) / bounds.height - .5) * 2)) * 4;
        if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
        this.pointerFrame = requestAnimationFrame(() => {
          visual.style.setProperty('--p1-move-x', `${x.toFixed(2)}px`);
          visual.style.setProperty('--p1-move-y', `${y.toFixed(2)}px`);
          this.pointerFrame = null;
        });
      }, { passive: true, signal });
      visual?.addEventListener('pointerleave', resetPointer, { signal });
      this.motionQuery.addEventListener?.('change', () => {
        if (!this.motionQuery.matches) return;
        showAll();
        this.revealObserver?.disconnect();
        resetPointer();
          this.activeAnimations.forEach((animation) => animation.cancel());
      }, { signal });
    }

    updateSticky() {
      if (!this.sticky || !this.purchase || !this.isConnected) return;
      const primaryPurchase = this.purchase.querySelector('[data-p1-purchase-action="checkout"]');
      const purchaseBounds = (primaryPurchase || this.purchase).getBoundingClientRect();
      const sectionBounds = this.getBoundingClientRect();
      const purchasePointVisible = Array.from(this.querySelectorAll('[data-p1-purchase-point]')).some((button) => {
        if (button.hidden || button.inert || button.closest('[hidden], [inert]')) return false;
        const bounds = button.getBoundingClientRect();
        return bounds.height > 0 && bounds.bottom > 0 && bounds.top < window.innerHeight;
      });
      this.sticky.hidden = purchasePointVisible || !(purchaseBounds.bottom < 0 && sectionBounds.bottom > window.innerHeight && sectionBounds.top < window.innerHeight);
    }

    updateNav() {
      const offset = window.innerWidth < 750 ? 145 : 170;
      let current = null;
      this.navItems.forEach((item) => { if (item.section.getBoundingClientRect().top <= offset) current = item; });
      this.navItems.forEach((item) => {
        if (item === current) item.link.setAttribute('aria-current', 'true');
        else item.link.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('zv-p1-pro', ZinVoltP1Pro);
})();
